import { useParams } from 'react-router';
import Box from '@mui/material/Box';
import { Alert, CircularProgress, IconButton, Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useNotifications } from '@/hooks/use-notifications';
import { MoreHoriz } from '@mui/icons-material';
import { useGetMediaQuery } from '@/state/queries/media/use-get-media.ts';
import MediaCard from '@/components/media/organization-media-list/media-card.tsx';
import CreateMediaForm from '@/components/media/organization-media-list/create-media.form.tsx';
import { MEDIA_PARENT_ENTITY } from '@/types/dtos/media/media-parent-entity.enum.ts';
import { useMediaProcessingSocketSync } from '@/hooks/use-media-processing-socket-sync';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { useEffect } from 'react';

export default function OrganizationMediaList() {
  const { organizationId } = useParams();
  const activeOrganization = useActiveOrganizationStore((state) => state.activeOrganization);

  if (!organizationId) {
    return <MissingOrganizationNotification />;
  }
  if (!activeOrganization || organizationId !== activeOrganization.id) {
    return null;
  }

  return <OrganizationMediaListForOrganization organizationId={organizationId} />;
}

function MissingOrganizationNotification() {
  const t = useTranslations();
  const notification = useNotifications();

  useEffect(() => {
    notification.notifyError(t('media.notification.missingOrganization'));
  }, [notification, t]);

  return null;
}

function OrganizationMediaListForOrganization({ organizationId }: { organizationId: string }) {
  const t = useTranslations();
  const notification = useNotifications();
  useMediaProcessingSocketSync(MEDIA_PARENT_ENTITY.ORGANIZATION, organizationId);
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetMediaQuery({
    parentId: organizationId!,
    parentType: MEDIA_PARENT_ENTITY.ORGANIZATION.toLowerCase(),
  });

  if (error) {
    notification.notifyError(t('media.notification.loadOrganizationMediaError'));
    return;
  }

  if (isLoading) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box sx={{ margin: 'auto 5%' }}>
      <CreateMediaForm parentId={organizationId} parentType={MEDIA_PARENT_ENTITY.ORGANIZATION} />
      {data?.pages[0].data.length === 0 && (
        <Alert variant="outlined" severity="info">
          <Typography variant="body1">{t('media.noOrganizationMedia')}</Typography>
        </Alert>
      )}
      {data?.pages.map((page) => page.data.map((media) => <MediaCard key={media.id} media={media} />))}
      <Stack alignItems="center" sx={{ mt: '1rem' }}>
        <IconButton size="small" onClick={() => fetchNextPage()} disabled={!hasNextPage || isFetchingNextPage}>
          {isFetchingNextPage ? <CircularProgress /> : hasNextPage ? <MoreHoriz /> : null}
        </IconButton>
      </Stack>
    </Box>
  );
}
